import { useState } from "react";
import { useCreateTopic } from "@/api/useGetTopics";
import { Button, Flex, Text } from "@radix-ui/themes";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Loader2Icon } from "lucide-react";
import { toast } from "sonner";

interface CreateTopicDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refetch: () => void;
}

export function CreateTopicDialog({
  open,
  onOpenChange,
  refetch,
}: CreateTopicDialogProps) {
  const { mutate: createTopic, isLoading } = useCreateTopic();
  const [topicName, setTopicName] = useState("");
  const [description, setDescription] = useState("");

  const closeDialog = () => {
    onOpenChange(false);
    setTopicName("");
    setDescription("");
  };

  const handleCreateTopic = async () => {
    if (!topicName.trim()) return;

    await createTopic(
      {
        topic: topicName.trim(),
        description: description.trim() || undefined,
      },
      {
        onSuccess: () => {
          refetch();
          closeDialog();
          toast(`Topic "${topicName}" created successfully`);
        },
        onError: () => {
          try {
            // window.quack?.proactiveTrigger();
          } catch (e) {}
          toast.error("Failed to create topic");
        },
      }
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-md">
        <Flex direction="column" gap="4">
          <Text size="5" weight="bold">
            Create New Topic
          </Text>
          <Flex direction="column" gap="3">
            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">
                Topic Name *
              </Text>
              <Input
                type="text"
                value={topicName}
                onChange={(e) => setTopicName(e.target.value)}
                placeholder="Enter topic name"
                className="w-full "
                autoFocus
                required
              />
            </Flex>
            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">
                Description
              </Text>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[100px]"
                placeholder="Enter topic description (optional)"
              />
            </Flex>
          </Flex>
          <Flex gap="3" justify="end">
            <Button
              variant="outline"
              color="gray"
              highContrast
              onClick={closeDialog}
              disabled={isLoading}
              className="text-gray-900 hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTopic}
              disabled={!topicName.trim() || isLoading}
              className="bg-black text-white hover:bg-black/90"
            >
              {isLoading ? (
                <Loader2Icon className="animate-spin" />
              ) : (
                "Create Topic"
              )}
            </Button>
          </Flex>
        </Flex>
      </div>
    </div>
  );
}
